/* eslint-disable class-methods-use-this */
/* eslint-disable import/extensions, flowtype/no-types-missing-file-annotation */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PaginationAccessor, SearchkitComponent } from 'searchkit';
import PulseLoader from 'react-spinners/PulseLoader';
import defaults from 'lodash/defaults';
import get from 'lodash/get';

class InfiniteScrollingPagination extends SearchkitComponent {
  static propTypes = defaults(
    {
      children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
      typeToScroll: PropTypes.string,
    },
    SearchkitComponent.propTypes,
  );

  static defaultProps = {
    children: null,
    typeToScroll: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      loader: null,
      root: null,
      needToLoadMore: false,
    };
    this.setLoaderRef = loader => {
      this.setState({ loader });
    };
    this.setRootRef = root => {
      this.setState({ root });
    };
  }

  onIntersectionEvent = entries => {
    const [entry] = entries;
    if (entry.isIntersecting && this.hasMore()) {
      this.setState({ needToLoadMore: true });

      const initialQuery = this.searchkit.accessors.queryAccessor.state.value;

      setTimeout(async () => {
        // The user might still be typing their query - only load the next page if the query remains the same as it was on the initial event.
        const queryHasChanged = () =>
          initialQuery !== this.searchkit.accessors.queryAccessor.state.value;

        if (this.hasMore() && !queryHasChanged()) {
          await this.showMoreIfNeeded();
          // On large screens we might need to load multiple pages, so keep running the handler
          // until the loading icon is pushed off screen
          if (this.hasMore() && !queryHasChanged() && this.state.needToLoadMore) {
            this.onIntersectionEvent(entries);
          }
        }
      }, 0);
    }
    if (!entry.isIntersecting) {
      this.setState({ needToLoadMore: false });
    }
  };

  componentWillUnmount() {
    super.componentWillUnmount();
    const { loader } = this.state;
    if (this.observer && loader) {
      this.observer.unobserve(loader);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observer = null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.root && !prevState.root) {
      // Check that the parent element is capable of overflowing. Otherwise we'll use the viewport as our root.
      const { parentNode } = this.state.root;
      const parentNodeStyle = getComputedStyle(parentNode);
      const parentCanOverflow =
        parentNodeStyle.overflow.includes('auto') || parentNodeStyle.overflow.includes('scroll');
      const options = {
        root: parentCanOverflow ? parentNode : null, // null means the viewport
        rootMargin: parentCanOverflow ? '550px' : undefined, // This will only have any effect if the root is parentNode.
        threshold: 0,
      };
      if (this.observer) {
        this.observer.disconnect();
      }
      this.observer = new IntersectionObserver(this.onIntersectionEvent, options);
    }

    if (this.observer && this.state.loader !== prevState.loader) {
      if (prevState.loader) {
        this.observer.unobserve(prevState.loader);
      }
      if (this.state.loader) {
        this.observer.observe(this.state.loader);
      }
    }

    if (this.observer && prevProps.searchIsActive !== this.props.searchIsActive) {
      if (prevProps.searchIsActive && prevState.loader) {
        this.observer.unobserve(prevState.loader);
      }
      if (this.props.searchIsActive && this.state.loader) {
        this.observer.observe(this.state.loader);
      }
    }
  }

  defineAccessor() {
    return new PaginationAccessor('p');
  }

  getCurrentPage() {
    if (!this.accessor) {
      return 1;
    }
    return Number(this.accessor.state.getValue()) || 1;
  }

  getTotalResults() {
    const { label } = this.searchkit.accessors.statefulAccessors;
    const { typeToScroll } = this.props;

    const buckets = label.getBuckets();
    const selected =
      buckets.find(bucket => bucket.selected) ||
      buckets.find(bucket => bucket.key === typeToScroll);

    return selected && selected.doc_count ? selected.doc_count : 1;
  }

  getTotalPages() {
    const total = this.getTotalResults();
    const size = get(this.getQuery(), 'query.size', 10);
    return Math.ceil(total / size);
  }

  hasMore() {
    return this.hasHits() && this.getTotalPages() > this.getCurrentPage();
  }

  showMoreIfNeeded = () => {
    if (!this.hasMore()) {
      return Promise.resolve();
    }
    this.accessor.state = this.accessor.state.setValue(this.getCurrentPage() + 1);
    const updateHistory = this.getCurrentPage() === 2;
    return this.searchkit.performSearch(true, true, updateHistory);
  };

  render() {
    const { children } = this.props;

    return (
      <div ref={this.setRootRef}>
        {children}
        {this.hasMore() ? (
          <div
            ref={this.setLoaderRef}
            style={{ textAlign: 'center' }}
            key="search-infinite-scroll-spinner"
            data-cy="search-infinite-scroll-spinner"
          >
            <PulseLoader loading color="#ff5725" sizeUnit="px" size={10} />
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  searchIsActive: state.search.isActive,
});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InfiniteScrollingPagination);
